import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  WorkflowAction, WorkflowLogEntry, WorkflowRunStatus, WorkflowTriggerType,
} from '@pm/shared';
import { api } from '@/lib/api';
import { homeBody, homeLists, keys, type Home } from '@/lib/query';
import type { ColumnDefinition } from '../boards/api';
import type { Draft, DraftStep, DraftTrigger } from './draft';

type Person = { id: string; name: string; avatarUrl: string | null };

export interface WorkflowSummary {
  id: string;
  /** Null for an organization's own workflow. */
  workspaceId: string | null;
  organizationId: string | null;
  folderId: string | null;
  name: string;
  isActive: boolean;
  triggerType: WorkflowTriggerType;
  triggerBoardId: string | null;
  createdById: string;
  updatedAt: string;
  /** Whether this person may change it - the API's answer, mirrored. */
  canEdit: boolean;
}

export interface WorkflowListItem extends WorkflowSummary {
  workspace: { id: string; name: string } | null;
  /** The default organization has no name until someone gives it one. */
  organization: { id: string; name: string | null } | null;
}

export interface WorkflowDetail extends WorkflowListItem {
  trigger: DraftTrigger;
  steps: DraftStep[];
  lastTriggeredAt: string | null;
  createdBy: Person;
  /** Schedules and date triggers that are on: when they fire next. */
  nextRunAt: string | null;
}

export interface WorkflowRun {
  id: string;
  status: WorkflowRunStatus;
  /** Name is null when the task's board is closed to the viewer. */
  item: { id: string; boardId: string; name: string | null } | null;
  /** Null for the same reason. Messages arrive translated. */
  log: (WorkflowLogEntry & { message: string | null })[] | null;
  error: string | null;
  resumeAt: string | null;
  createdAt: string;
  finishedAt: string | null;
}

export interface WorkflowBoardOption {
  id: string;
  name: string;
  isPrivate: boolean;
  workspace: { id: string; name: string };
}

export interface BoardGroupOption { id: string; name: string }

export const useWorkspaceWorkflows = (workspaceId: string) =>
  useQuery({
    queryKey: keys.workspaceWorkflows(workspaceId),
    queryFn: () => api.get<WorkflowSummary[]>(`/workspaces/${workspaceId}/workflows`),
    enabled: Boolean(workspaceId),
  });

export const useOrganizationWorkflows = (organizationId: string) =>
  useQuery({
    queryKey: keys.organizationWorkflows(organizationId),
    queryFn: () => api.get<WorkflowSummary[]>(`/organizations/${organizationId}/workflows`),
    enabled: Boolean(organizationId),
  });

export const useAllWorkflows = () =>
  useQuery({ queryKey: keys.workflows, queryFn: () => api.get<WorkflowListItem[]>('/workflows') });

export const useWorkflow = (id: string) =>
  useQuery({
    queryKey: keys.workflow(id),
    queryFn: () => api.get<WorkflowDetail>(`/workflows/${id}`),
    enabled: Boolean(id),
  });

/** Polls while a run is still on its way, so the history settles by itself. */
export const useWorkflowRuns = (id: string) =>
  useQuery({
    queryKey: keys.workflowRuns(id),
    queryFn: () => api.get<WorkflowRun[]>(`/workflows/${id}/runs`),
    enabled: Boolean(id),
    refetchInterval: (query) =>
      query.state.data?.some((r) => r.status === 'queued' || r.status === 'running') ? 3000 : false,
  });

/** Which actions the server offers - posting to the PM channel only once it is set up. */
export const useWorkflowCatalog = () =>
  useQuery({
    queryKey: keys.workflowCatalog,
    queryFn: () => api.get<{ actions: WorkflowAction[] }>('/workflows/catalog'),
    staleTime: 10 * 60_000,
  });

/** Every board this person can open, across workspaces. */
export const useWorkflowBoards = () =>
  useQuery({
    queryKey: keys.workflowBoards,
    queryFn: () => api.get<WorkflowBoardOption[]>('/workflows/boards'),
    staleTime: 60_000,
  });

/** A board's columns and groups, for the pickers. Fails quietly on a board the viewer cannot open. */
export function useBoardMeta(boardId: string | undefined) {
  const id = boardId ?? '';
  const columns = useQuery({
    queryKey: keys.boardColumns(id),
    queryFn: () => api.get<ColumnDefinition[]>(`/boards/${id}/columns`),
    enabled: Boolean(id),
    staleTime: 60_000,
  });
  const groups = useQuery({
    queryKey: keys.boardGroups(id),
    queryFn: () => api.get<BoardGroupOption[]>(`/boards/${id}/groups`),
    enabled: Boolean(id),
    staleTime: 60_000,
  });
  return {
    columns: columns.data ?? [],
    groups: groups.data ?? [],
    isError: columns.isError || groups.isError,
  };
}

export function useCreateWorkflow(home: Home) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Draft & { folderId: string | null; isActive: boolean }) =>
      api.post<WorkflowSummary>('/workflows', { ...body, ...homeBody(home) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: homeLists(home).workflows });
      void qc.invalidateQueries({ queryKey: keys.workflows });
    },
  });
}

export function useWorkflowActions(id: string, home: Home) {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: keys.workflow(id), exact: true });
    void qc.invalidateQueries({ queryKey: homeLists(home).workflows });
    void qc.invalidateQueries({ queryKey: keys.workflows });
  };
  return {
    update: useMutation({
      mutationFn: (body: Partial<Draft> & { isActive?: boolean; folderId?: string | null }) =>
        api.put<WorkflowSummary>(`/workflows/${id}`, body),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: () => api.delete(`/workflows/${id}`),
      onSuccess: () => {
        qc.removeQueries({ queryKey: keys.workflow(id) });
        void qc.invalidateQueries({ queryKey: homeLists(home).workflows });
        void qc.invalidateQueries({ queryKey: keys.workflows });
      },
    }),
  };
}

/** Moving a workflow between folders, from the workspace or organization page. */
export function useMoveWorkflow(home: Home) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, folderId }: { id: string; folderId: string | null }) =>
      api.put(`/workflows/${id}`, { folderId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: homeLists(home).workflows });
      void qc.invalidateQueries({ queryKey: homeLists(home).folders });
    },
  });
}
